<div class="custom-section card runtime-form">
  <div *ngIf="loading" class="runtime-muted">Loading workflow...</div>
  <div *ngIf="errorMessage" class="runtime-error">{{ errorMessage }}</div>

  <ng-container *ngIf="!loading && workflow">
    <div class="runtime-header">
      <div>
        <h1 class="runtime-title">
          {{ workflow.name }}
          <!-- D3: share a link straight to this item — opens the app when installed. -->
          <button
            type="button"
            class="runtime-copy-link"
            *ngIf="canCopyLink"
            [disabled]="linkIssuing"
            (click)="openShare()"
            [attr.aria-label]="'Share a link to this workflow item'"
            [title]="linkIssuing ? 'Creating link...' : 'Share a link to this workflow'"
          >
            <wf-icon [name]="linkCopied ? 'check' : 'link'" [size]="16"></wf-icon>
          </button>
          <!-- D3: a workflow nobody has started yet is still shareable — provisions a blank
               pending item, then opens its instance view where Copy Link is available. -->
          <button
            type="button"
            class="runtime-copy-link"
            *ngIf="mode === 'definition'"
            [disabled]="startingPendingItem"
            (click)="startAndShareWorkflow()"
            [attr.aria-label]="'Share a link to this workflow'"
            [title]="startingPendingItem ? 'Preparing link...' : 'Share a link to this workflow'"
          >
            <wf-icon name="link" [size]="16"></wf-icon>
          </button>
        </h1>
        <p>{{ workflow.description || "No description" }}</p>
        <span>Version {{ workflow.version }} · Stage {{ stageNumber }}</span>

        <div class="runtime-status-chips" *ngIf="isReadOnly || escalated || isOverdue">
          <span class="runtime-chip runtime-chip--muted" *ngIf="isReadOnly">
            <wf-icon name="eye" [size]="14"></wf-icon>
            Read-only for you
          </span>

          <!-- RT-17: escalation visibility — this item has been escalated to its target role. -->
          <span class="runtime-chip runtime-chip--escalated" *ngIf="escalated">
            <wf-icon name="zap" [size]="14"></wf-icon>
            Escalated<ng-container *ngIf="escalatedTarget"> to {{ escalatedTarget }}</ng-container>
          </span>

          <!-- RT-21: the current stage has breached its SLA deadline. -->
          <span
            class="runtime-chip runtime-chip--overdue"
            *ngIf="isOverdue"
            title="This stage has passed its time budget">
            <wf-icon name="clock" [size]="14"></wf-icon>
            Overdue<ng-container *ngIf="slaDeadline"> · due {{ slaDeadline | date: "short" }}</ng-container>
          </span>
        </div>
      </div>

      <button
        type="button"
        class="runtime-activity-toggle"
        *ngIf="fromHistory && mode === 'instance'"
        [class.is-open]="showActivity"
        [attr.aria-expanded]="showActivity"
        (click)="toggleActivity()"
      >
        <wf-icon name="history" [size]="15"></wf-icon>
        <span>Activity history</span>
      </button>
    </div>

    <!-- N7: activity timeline panel, revealed beneath the toggle. -->
    <div class="runtime-activity runtime-activity--top" *ngIf="fromHistory && mode === 'instance' && showActivity">
      <ul class="runtime-activity-list" *ngIf="activity.length; else noActivityTop">
        <li class="runtime-activity-event is-clickable" *ngFor="let event of activity; let i = index"
          [ngClass]="'runtime-activity--' + event.action.toLowerCase()"
          [class.is-selected]="selectedActivity === i"
          (click)="selectActivity(i)">
          <span class="runtime-activity-dot"></span>
          <div class="runtime-activity-body">
            <div class="runtime-activity-line">
              <span class="runtime-activity-label">{{ event.label }}</span>
              <span class="runtime-activity-stage" *ngIf="event.stageNumber">· Stage {{ event.stageNumber }}</span>
            </div>
            <div class="runtime-activity-meta">
              <span class="runtime-activity-actor">{{ event.actor }}</span>
              <span class="runtime-activity-time">· {{ event.timestampUtc | date: "MMM d, y, h:mm a" }}</span>
            </div>
            <div class="runtime-activity-comment" *ngIf="event.detail">“{{ event.detail }}”</div>
          </div>
        </li>
      </ul>
      <ng-template #noActivityTop>
        <p class="runtime-muted">No activity yet.</p>
      </ng-template>
    </div>

    <div *ngIf="returnReason" class="runtime-return-banner">
      Returned: {{ returnReason }}
    </div>

    <!-- Conditional Visibility: recompute on any committed interaction within the form (native
         change events for selects/checkboxes/radios/dates, focusout for free-text fields left
         after typing, click for custom option-based selectors). Delegated at this wrapper rather
         than touching the shared log-sheet-form component. -->
    <div (change)="recomputeFieldVisibility()" (focusout)="recomputeFieldVisibility()" (click)="recomputeFieldVisibility()">
      <app-log-sheet-form
        [runtimeMode]="true"
        [runtimeEditable]="isEditable()"
        [runtimeNotesAllowed]="hasAnyAction()"
        [runtimeSections]="runtimeSections"
      >
      </app-log-sheet-form>
    </div>

    <div class="runtime-actions" *ngIf="hasAnyAction(); else readOnlyActions">
      <button
        type="button"
        class="btn-outline runtime-back-btn"
        [disabled]="saving"
        (click)="goBack()"
      >
        Back
      </button>
      <button
        type="button"
        class="btn-fill"
        *ngIf="mode === 'definition' || canSubmit() || canResubmit()"
        [disabled]="saving"
        (click)="submitWorkflow()"
      >
        <span
          *ngIf="saving"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        {{ canResubmit() ? "Continue / Re-submit" : "Submit" }}
      </button>
      <button
        type="button"
        class="btn-outline"
        *ngIf="canSaveDraft()"
        [disabled]="saving || draftSaving"
        (click)="saveDraft()"
      >
        <span
          *ngIf="draftSaving"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        Save Draft
      </button>
      <button
        type="button"
        class="btn-outline"
        *ngIf="canSaveDraft() && hasSavedDraft"
        [disabled]="saving || draftSaving"
        (click)="discardDraft()"
      >
        Discard Draft
      </button>
      <button
        type="button"
        class="btn-fill"
        *ngIf="canApprove()"
        [disabled]="saving"
        (click)="requestAction('approve')"
      >
        <img class="action-icon" src="assets/check-mark.svg" alt="" />
        Approve
      </button>
      <button
        type="button"
        class="btn-outline"
        *ngIf="canReturn()"
        [disabled]="saving"
        (click)="requestAction('return')"
      >
        <img class="action-icon" src="assets/back-arrow-icon.svg" alt="" />
        Return
      </button>
      <button
        type="button"
        class="btn-danger"
        *ngIf="canReject()"
        [disabled]="saving"
        (click)="requestAction('reject')"
      >
        <img class="action-icon" src="assets/close.svg" alt="" />
        Reject
      </button>
    </div>
    <ng-template #readOnlyActions>
      <div class="runtime-actions">
        <button
          type="button"
          class="btn-outline runtime-back-btn"
          [disabled]="saving"
          (click)="goBack()"
        >
          Back
        </button>
      </div>
    </ng-template>

    <div class="runtime-notes" *ngIf="mode === 'instance' && !isGuest && runtimeItem?.outputs?.length">
      <h2 class="runtime-notes-title">Final outputs</h2>
      <ul class="runtime-notes-list">
        <li class="runtime-note" *ngFor="let output of runtimeItem?.outputs">
          <div class="runtime-note-meta">
            <span class="runtime-note-author">{{ output.fileName || output.format }}</span>
            <span class="runtime-note-time">{{ output.status }}</span>
          </div>
          <button
            type="button"
            class="btn-outline"
            *ngIf="output.downloadable"
            [disabled]="downloadingOutputId === output.workflowOutputId"
            (click)="downloadOutput(output)"
          >
            {{ downloadingOutputId === output.workflowOutputId ? "Downloading..." : "Download" }}
          </button>
        </li>
      </ul>
      <button
        type="button"
        class="btn-outline"
        *ngIf="canRegenerateOutputs"
        [disabled]="regeneratingOutputs"
        (click)="regenerateOutputs()"
      >
        {{ regeneratingOutputs ? "Regenerating..." : "Regenerate flagged outputs" }}
      </button>
    </div>

    <div class="runtime-notes" *ngIf="mode === 'instance' && !isGuest">
      <div class="runtime-note-add" *ngIf="hasAnyAction()">
        <label class="custom-label" for="workflow-comment">Comment &amp; Advance</label>
        <div class="runtime-note-add-row">
          <textarea
            id="workflow-comment"
            class="custom-input"
            rows="2"
            [disabled]="noteSaving"
            [(ngModel)]="newNote"
            placeholder="Add a comment for reviewers..."
          ></textarea>
          <button
            type="button"
            class="btn-outline runtime-note-add-btn"
            [disabled]="noteSaving || !newNote.trim()"
            (click)="addNote()"
          >
            {{ noteSaving ? "Adding..." : "Add comment" }}
          </button>
        </div>
      </div>

      <ul class="runtime-notes-list" *ngIf="notes.length; else noNotes">
        <li class="runtime-note" *ngFor="let note of notes">
          <div class="runtime-note-meta">
            <span class="runtime-note-author">{{ note.author }}</span>
            <span class="runtime-note-time">{{ note.timestampUtc | date: "short" }}</span>
          </div>
          <p class="runtime-note-text">{{ note.text }}</p>
        </li>
      </ul>
      <ng-template #noNotes>
        <p class="runtime-muted">No comments yet.</p>
      </ng-template>
    </div>
  </ng-container>
</div>

<div class="runtime-modal-backdrop" *ngIf="shareOpen" (click)="closeShare()">
  <div class="runtime-modal" (click)="$event.stopPropagation()">
    <div class="runtime-modal-head">
      <wf-icon name="link" [size]="18"></wf-icon>
      <h2>Share this workflow</h2>
    </div>
    <p class="runtime-modal-body">
      Anyone signed in can open this link. To let someone <strong>without an account</strong> open it,
      add their email — they’ll verify with a one-time code sent to that address.
    </p>

    <div class="runtime-modal-reason">
      <label class="runtime-modal-reason-label" for="workflow-share-email">
        Recipient email (optional)
      </label>
      <input
        id="workflow-share-email"
        class="custom-input"
        type="email"
        autocomplete="off"
        placeholder="name@example.com"
        [disabled]="linkIssuing"
        [(ngModel)]="shareEmail"
        (keyup.enter)="copyItemLink()" />
      <p class="runtime-modal-reason-missing" *ngIf="!shareEmailIsValid">
        Enter a valid email address, or leave it blank for a link only signed-in people can open.
      </p>
    </div>

    <p class="runtime-error" *ngIf="errorMessage">{{ errorMessage }}</p>

    <div class="runtime-modal-manual-copy" *ngIf="manualCopyUrl">
      <label class="runtime-modal-reason-label" for="workflow-share-url">Workflow link</label>
      <input
        id="workflow-share-url"
        class="custom-input"
        type="text"
        readonly
        [value]="manualCopyUrl"
        (focus)="$event.target.select()" />
    </div>

    <div class="runtime-modal-actions">
      <!-- Never disabled: a slow link request must not trap the user in the dialog. -->
      <button type="button" class="btn-outline" (click)="closeShare()">
        Cancel
      </button>
      <button
        type="button"
        class="btn-fill"
        [disabled]="linkIssuing || !shareEmailIsValid"
        (click)="copyItemLink()">
        {{ linkIssuing ? 'Creating link…' : (manualCopyUrl ? 'Try copy again' : 'Copy link') }}
      </button>
    </div>
  </div>
</div>

<!-- #6: confirm a stage action (approve / return / reject) before it commits. -->
<div class="runtime-modal-backdrop" *ngIf="pendingAction" (click)="cancelAction()">
  <div class="runtime-modal" (click)="$event.stopPropagation()">
    <div class="runtime-modal-head">
      <img class="runtime-modal-icon" src="assets/alert.svg" alt="" />
      <h2>{{ pendingActionLabel }} this workflow?</h2>
    </div>
    <p class="runtime-modal-body">
      This will <strong>{{ pendingActionLabel | lowercase }}</strong> the submission and move it to its
      next step. This action cannot be undone.
    </p>
    <div class="runtime-modal-reason" *ngIf="pendingActionNeedsReason">
      <label class="runtime-modal-reason-label" for="workflow-action-reason">Reason</label>
      <textarea
        id="workflow-action-reason"
        class="custom-input"
        rows="3"
        [disabled]="saving"
        [placeholder]="'Add a reason to ' + (pendingActionLabel | lowercase) + ' this submission'"
        [(ngModel)]="reason"
      ></textarea>
      <p class="runtime-modal-reason-missing" *ngIf="!reason.trim()">
        A reason is required before you can {{ pendingActionLabel | lowercase }}.
      </p>
    </div>
    <div class="runtime-modal-actions">
      <button type="button" class="btn-outline" (click)="cancelAction()">Cancel</button>
      <button
        type="button"
        [ngClass]="pendingAction === 'reject' ? 'btn-danger' : 'btn-fill'"
        [disabled]="!canConfirmPendingAction || saving"
        (click)="confirmAction()"
      >
        {{ pendingActionLabel }}
      </button>
    </div>
  </div>
</div>

<!-- TESTER-2: submission guard duplicate warning modal -->
<div class="runtime-modal-backdrop" *ngIf="showDuplicateModal" (click)="dismissDuplicateModal()">
  <div class="runtime-modal" (click)="$event.stopPropagation()">
    <div class="runtime-modal-head">
      <img class="runtime-modal-icon" src="assets/alert.svg" alt="" />
      <h2>Duplicate submission blocked</h2>
    </div>
    <p class="runtime-modal-body">{{ duplicateWarning }}</p>
    <div class="runtime-modal-actions">
      <button type="button" class="btn-fill" (click)="dismissDuplicateModal()">
        Got it
      </button>
    </div>
  </div>
</div>

<app-success-toast *ngIf="toastService.showToast"></app-success-toast>
